"use client";
import React from "react";
import { Box, Typography, Button, Container, Chip } from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import dynamic from "next/dynamic";
import { useInView } from "react-intersection-observer";
import { useTranslation } from "react-i18next";
import "../app/globals.css";
import { useRouter } from "next/navigation";
import { IMAGES } from "../constants/images";

const Chart = dynamic(() => import("react-apexcharts"), { ssr: false });

const GradientBackground = styled(Box)(({ theme }) => ({
  backgroundImage: `url('${IMAGES.COMPARISON_CHART_BG}')`,
  backgroundSize: "cover",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
  position: "relative",
  overflow: "hidden",
  width: "100%",
  marginTop: "5%",
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  paddingTop: "60px",
  paddingBottom: "40px",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    paddingTop: "40px",
    paddingBottom: "30px",
  },
  [theme.breakpoints.down("sm")]: {
    paddingTop: "30px",
    paddingBottom: "20px",
  },
}));

const ChartSection = styled(Box)(({ theme }) => ({
  paddingBottom: "80px",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    paddingBottom: "60px",
  },
  [theme.breakpoints.down("sm")]: {
    paddingBottom: "40px",
  },
}));

const SignUpButton = styled(Button)(({ theme }) => ({
  background: "#AC74FF",
  borderRadius: 25,
  color: "white",
  padding: "12px 30px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "15px",
  marginTop: "107%",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    background: "#fff",
    color: "#9B58FF",
    transform: "scale(1.05) translateY(-2px)",
  },
  [theme.breakpoints.down("md")]: {
    padding: "10px 24px",
    fontSize: "14px",
    marginTop: "0",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "8px 20px",
    fontSize: "13px",
    width: "100%",
    marginTop: "0",
  },
}));

const ComparisonChart = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 });
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const router = useRouter();

  // Pull translated data from JSON
  const categories = t("comparisonChart.categories", { returnObjects: true });
  const traditionalLabels = t("comparisonChart.dataLabels.traditional", {
    returnObjects: true,
  });
  const getgeekLabels = t("comparisonChart.dataLabels.getgeek", {
    returnObjects: true,
  });

  const chartOptions = {
    chart: {
      type: "bar",
      height: 400,
      background: "transparent",
      toolbar: { show: false },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 1200,
        animateGradually: { enabled: true, delay: 150 },
        dynamicAnimation: { enabled: true, speed: 350 },
      },
    },
    colors: ["#9CA3AF", "#9B59D0"],
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "50%",
        endingShape: "rounded",
        borderRadius: 10,
        borderRadiusApplication: "end",
        dataLabels: { position: "top" },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val, opts) {
        return opts.seriesIndex === 0
          ? traditionalLabels[opts.dataPointIndex]
          : getgeekLabels[opts.dataPointIndex];
      },
      offsetY: -25,
      style: {
        fontSize: "15px",
        colors: ["#ffffff"],
        fontWeight: 500,
        fontFamily: "var(--font-outfit)",
      },
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    xaxis: {
      categories: categories,
      labels: {
        style: {
          colors: "#ffffff",
          fontSize: "15px",
          fontWeight: 400,
          fontFamily: "var(--font-outfit)",
        },
        offsetY: 8,
        rotate: 0,
        trim: false,
        hideOverlappingLabels: false,
        maxHeight: 80,
        formatter: function (value) {
          if (value && value.length > 20) {
            const words = value.split(" ");
            let line1 = "",
              line2 = "",
              currentLine = "";
            words.forEach((word, index) => {
              if ((currentLine + word).length <= 20) {
                currentLine += (currentLine ? " " : "") + word;
              } else {
                line1 = currentLine;
                line2 += (line2 ? " " : "") + word;
                currentLine = line2;
              }
              if (index === words.length - 1) {
                if (!line1) line1 = currentLine;
                else line2 = currentLine;
              }
            });
            return [line1, line2];
          }
          return value;
        },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { show: false },
    fill: {
      type: "gradient",
      gradient: {
        shade: "dark",
        type: "vertical",
        shadeIntensity: 0.5,
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 1,
        stops: [0, 100],
        colorStops: [
          [
            { offset: 0, color: "#ACACAC", opacity: 1 },
            { offset: 100, color: "#FFFFFF", opacity: 0 },
          ],
          [
            { offset: 0, color: "#AC74FF", opacity: 1 },
            { offset: 100, color: "#1F1E20", opacity: 0 },
          ],
        ],
      },
    },
    tooltip: { enabled: false },
    legend: { show: false },
    grid: {
      show: true,
      borderColor: "rgba(255, 255, 255, 0.06)",
      strokeDashArray: 4,
      position: "back",
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: true } },
    },
    responsive: [
      {
        breakpoint: 900,
        options: {
          plotOptions: { bar: { columnWidth: "60%" } },
          xaxis: { labels: { style: { fontSize: "12px" }, rotate: 0 } },
          dataLabels: { style: { fontSize: "12px" }, offsetY: -20 },
          legend: { fontSize: "14px", offsetY: 25 },
        },
      },
      {
        breakpoint: 600,
        options: {
          chart: { height: 400 },
          plotOptions: { bar: { columnWidth: "60%", borderRadius: 6 } },
          xaxis: {
            labels: {
              style: { fontSize: "10px" },
              rotate: -45,
              rotateAlways: true,
              trim: false,
              hideOverlappingLabels: false,
              offsetX: 0,
              offsetY: 5,
            },
          },
          dataLabels: {
            enabled: true,
            style: { fontSize: "10px" },
            offsetY: -15,
          },
          legend: {
            fontSize: "11px",
            offsetY: 15,
            itemMargin: { horizontal: 10, vertical: 8 },
            markers: { width: 10, height: 10 },
          },
        },
      },
      {
        breakpoint: 480,
        options: {
          chart: { height: 380 },
          plotOptions: { bar: { columnWidth: "65%", borderRadius: 5 } },
          xaxis: {
            labels: {
              style: { fontSize: "9px" },
              rotate: -45,
              rotateAlways: true,
              trim: false,
              hideOverlappingLabels: false,
              offsetX: 0,
              offsetY: 5,
            },
          },
          dataLabels: {
            enabled: true,
            style: { fontSize: "9px" },
            offsetY: -12,
          },
          legend: {
            fontSize: "10px",
            offsetY: 12,
            itemMargin: { horizontal: 8, vertical: 6 },
            markers: { width: 8, height: 8 },
          },
        },
      },
    ],
  };

  const chartSeries = [
    { name: t("comparisonChart.legendTraditional"), data: [60, 30, 40, 67] },
    { name: t("comparisonChart.legendGetgeek"), data: [6, 2, 3, 9] },
  ];

  return (
    <GradientBackground
      role="img"
      aria-label="AI recruiting tool vs traditional hiring - 7 days vs 47 days comparison using recruitment automation software"
      sx={{ direction: isArabic ? "rtl" : "ltr" }}
    >
      {/* Header Section */}
      <HeaderSection>
        <Container maxWidth="lg">
          <Box
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "flex-start" }}
            gap={{ xs: 2, sm: 2.5, md: 3 }}
            sx={{ px: { xs: 2, sm: 3, md: 0 } }}
          >
            <Box maxWidth={{ xs: "100%", lg: 750 }}>
              <Chip
                label={t("comparisonChart.chip")}
                sx={{
                  backgroundColor: "transparent",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  mb: { xs: 1.5, sm: 2 },
                  color: "#FFFFFF",
                  fontSize: { xs: "10px", sm: "12px", md: "14px", lg: "16px" },
                  fontWeight: "500",
                  textTransform: "capitalize",
                  px: { xs: 0.4, sm: 0.6, md: 0.8, lg: 1 },
                  py: { xs: 0.8, sm: 1, md: 1.5, lg: 2.7 },
                  borderRadius: "40px",
                  fontFamily: "var(--font-outfit)",
                }}
              />
              <Typography
                variant="h3"
                color="white"
                fontWeight="700"
                lineHeight="1.3"
                mb={{ xs: 1.5, sm: 2, md: 2.5 }}
                sx={{
                  fontSize: {
                    xs: "1.15rem",
                    sm: "1.25rem",
                    md: "1.75rem",
                    lg: "2rem",
                    xl: "2.5rem",
                  },
                  mt: { xs: "3%", sm: "2.5%", md: "2%" },
                  textAlign: isArabic ? "right" : "left",
                  fontFamily: "var(--font-plusjakarta)",
                }}
              >
                {t("comparisonChart.title")}
              </Typography>
              <Typography
                variant="body1"
                color="#D1D5DB"
                sx={{
                  lineHeight: 1.7,
                  fontWeight: 400,
                  fontSize: {
                    xs: "12px",
                    sm: "13px",
                    md: "15px",
                    lg: "15px",
                    xl: "19px",
                  },
                  textAlign: isArabic ? "right" : "left",
                  fontFamily: "var(--font-outfit)",
                }}
              >
                {t("comparisonChart.subtitle")}
              </Typography>
            </Box>

            <Box
              display="flex"
              alignItems="center"
              sx={{
                flexShrink: 0,
                alignSelf: { xs: "stretch", md: "flex-start" },
                width: { xs: "100%", md: "auto" },
              }}
            >
              <SignUpButton
                variant="contained"
                endIcon={
                  isArabic ? (
                    <ArrowForwardIcon
                      sx={{
                        fontSize: { xs: 18, sm: 20 },
                        transform: "rotate(180deg)",
                      }}
                    />
                  ) : (
                    <ArrowForwardIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                  )
                }
                sx={{
                  marginTop: isArabic ? "70%" : undefined,
                  "& .MuiButton-endIcon": {
                    marginLeft: isArabic ? "12px" : "8px",
                    marginRight: isArabic ? "4px" : "0px",
                  },
                }}
                onClick={() => router.push("/free-trial-of-ai-recruiting-tool")}
              >
                {t("comparisonChart.cta")}
              </SignUpButton>
            </Box>
          </Box>
        </Container>
      </HeaderSection>

      {/* Chart Section */}
      <ChartSection>
        <Container maxWidth="lg">
          <Box
            ref={ref}
            sx={{
              borderRadius: { xs: "12px", sm: "14px", md: "16px" },
              padding: {
                xs: "15px 5px",
                sm: "20px 10px",
                md: "30px 20px",
                lg: "50px 10px",
              },
              backdropFilter: "blur(10px)",
              position: "relative",
              overflow: "hidden",
              mx: { xs: 2, sm: 3, md: 0 },
            }}
          >
            <Box
              height={{ xs: 400, sm: 400, md: 420 }}
              width={{ xs: "100%", md: "108%" }}
              sx={{
                mr: isArabic ? { xs: 0, md: "-9%" } : { xs: 0, md: "-1%" },
              }}
            >
              {inView && (
                <Chart
                  options={chartOptions}
                  series={chartSeries}
                  type="bar"
                  height="100%"
                  width="100%"
                />
              )}
            </Box>

            {/* Legend */}
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={3}
              mt={5}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  width="1.1rem"
                  height="1.1rem"
                  bgcolor="#B8B8C0"
                  borderRadius="3px"
                />
                <Typography color="white" fontFamily="Outfit">
                  {t("comparisonChart.legendTraditional")}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  width="1.1rem"
                  height="1.1rem"
                  bgcolor="#AC74FF"
                  borderRadius="3px"
                />
                <Typography color="white" fontFamily="Outfit">
                  {t("comparisonChart.legendGetgeek")}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </ChartSection>
    </GradientBackground>
  );
};

export default ComparisonChart;
